import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import SearchInput from "../../../../../components/SearchInput";
import axiosClient from "../../../../../utils/api/axiosClient";
import { defaultPageModel, pageSizeOptions } from "../../../../../utils/constant";
import { getToken } from "../../../../../utils/functions";
import { MemberData } from "../types";
import CustomSelect from "../../../../../components/CustomSelect";
import FilterListIcon from "@mui/icons-material/FilterList";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CustomModal from "../../../../../components/CustomModal";
import CustomButton from "../../../../../components/CustomButton";
import CustomInput from "../../../../../components/CustomInput";

type SchedulerMemberRow = MemberData & {
  name: string;
  hasDP: boolean;
  hasApp: boolean;
  profileType: string;
};

type SchedulerFilter = {
  profileTypes: string[];
  hasDP: boolean;
  hasApp: boolean;
  genders: string[];
  createdFrom: string;
  createdTo: string;
};

type ScheduleHistoryItem = {
  id: number;
  templateId?: string;
  apiCode?: string;
  campaignName?: string;
  mediaUrl?: string;
  templateName: string;
  scheduledAt: string;
  status: string;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  created_at?: string;
};

const EMPTY_FILTER: SchedulerFilter = {
  profileTypes: [],
  hasDP: false,
  hasApp: false,
  genders: [],
  createdFrom: "",
  createdTo: "",
};

const PROFILE_TYPE_OPTIONS = ["premium", "free", "expired", "deleted"];
const GENDER_OPTIONS = ["MALE", "FEMALE"];

const HEADERS = [
  { label: "Member ID", field: "memberId", sortable: true },
  { label: "Name", field: "firstName", sortable: true },
  { label: "Phone", field: "mobile", sortable: true },
  { label: "Has DP", field: "hasDP", sortable: false },
  { label: "Gender", field: "gender", sortable: true },
  { label: "Created Date", field: "created_at", sortable: true },
  { label: "Has App", field: "hasApp", sortable: false },
  { label: "Profile Type", field: "profileType", sortable: false },
];

const cellSx = {
  py: "6px",
  px: "8px",
  fontSize: "0.8rem",
  whiteSpace: "nowrap",
  borderBottom: "1px solid #e5e7eb",
};

const headSx = {
  ...cellSx,
  fontWeight: 700,
  bgcolor: "#212529",
  color: "#ffffff",
  py: "8px",
  fontSize: "0.82rem",
  cursor: "pointer",
};

function getProfileType(row: MemberData): string {
  if (row.memberType) return row.memberType;
  if (row.deleted_at) return "deleted";
  if (row.status === "ACTIVE") return "premium";
  if (row.status === "EXPIRED") return "expired";
  return "free";
}

function hasDisplayPicture(row: MemberData): boolean {
  if (typeof row?.hasProfileImage === "boolean") return row.hasProfileImage;
  if (row?.profile?.img || row?.profile?.profileImage || row?.profile?.profilePicture) return true;
  if (Array.isArray(row?.files)) {
    return row.files.some((file: any) => {
      const galleryType = String(file?.galleryType || file?.type || "").toUpperCase();
      const fileName = String(file?.fileName || "").toLowerCase();
      return galleryType === "PROFILE_IMAGE" || fileName.includes("profile");
    });
  }
  return false;
}

export default function SchedulerMembers() {
  const [pageModel, setPageModel] = React.useState(defaultPageModel);
  const [isLoading, setIsLoading] = React.useState(false);
  const [rows, setRows] = React.useState<SchedulerMemberRow[]>([]);
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [sortField, setSortField] = React.useState<string>("id");
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">("desc");
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [filterModalOpen, setFilterModalOpen] = React.useState(false);
  const [appliedFilter, setAppliedFilter] = React.useState<SchedulerFilter>(EMPTY_FILTER);
  const [draftFilter, setDraftFilter] = React.useState<SchedulerFilter>(EMPTY_FILTER);
  const [sendModalOpen, setSendModalOpen] = React.useState(false);
  const [historyModalOpen, setHistoryModalOpen] = React.useState(false);
  const [scheduleDateTime, setScheduleDateTime] = React.useState("");
  const [scheduleTemplateId, setScheduleTemplateId] = React.useState("");
  const [scheduleApiCode, setScheduleApiCode] = React.useState("IndianYadavs");
  const [scheduleCampaignName, setScheduleCampaignName] = React.useState("");
  const [scheduleMediaUrl, setScheduleMediaUrl] = React.useState("");
  const [isSubmittingSchedule, setIsSubmittingSchedule] = React.useState(false);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [scheduleHistory, setScheduleHistory] = React.useState<ScheduleHistoryItem[]>([]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [debouncedSearch]);

  const fetchData = React.useCallback(() => {
    const headers = { Authorization: getToken() };
    setIsLoading(true);

    axiosClient
      .post(
        `/v1/users/getSchedulerMembers/${pageModel.page + 1}/${pageModel.pageSize}`,
        {
          profileTypes: appliedFilter.profileTypes,
          hasDP: appliedFilter.hasDP,
          hasApp: appliedFilter.hasApp,
          genders: appliedFilter.genders,
          createdFrom: appliedFilter.createdFrom || null,
          createdTo: appliedFilter.createdTo || null,
        },
        {
          headers,
          skipGlobalLoader: true,
          params: {
            search: debouncedSearch,
            sortField,
            sortDirection,
          },
        }
      )
      .then((res) => {
        const content = (res?.data?.content || []) as MemberData[];
        const transformedRows: SchedulerMemberRow[] = content.map((row) => ({
          ...row,
          name: `${row.firstName || ""} ${row.lastName || ""}`.trim(),
          hasDP: hasDisplayPicture(row),
          hasApp: Boolean(row.hasAndroidApp || row.hasIosApp),
          profileType: getProfileType(row),
        }));
        setRows(transformedRows);
        setTotalRows(res?.data?.totalElements || 0);
        setTotalPages(res?.data?.totalPages || 1);
      })
      .catch((error) => {
        console.log(error);
        setRows([]);
        setTotalRows(0);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [pageModel, debouncedSearch, sortField, sortDirection, appliedFilter]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (field: string, sortable: boolean) => {
    if (!sortable) return;
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPageModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleOpenFilter = () => {
    setDraftFilter(appliedFilter);
    setFilterModalOpen(true);
  };

  const handleApplyFilter = () => {
    setAppliedFilter(draftFilter);
    setPageModel((prev) => ({ ...prev, page: 0 }));
    setFilterModalOpen(false);
  };

  const handleClearFilter = () => {
    setDraftFilter(EMPTY_FILTER);
    setAppliedFilter(EMPTY_FILTER);
    setPageModel((prev) => ({ ...prev, page: 0 }));
    setFilterModalOpen(false);
  };

  const loadScheduleHistory = React.useCallback(() => {
    const headers = { Authorization: getToken() };
    setHistoryLoading(true);
    axiosClient
      .get("/v1/users/scheduler-whatsapp/schedules/1/100", { headers, skipGlobalLoader: true })
      .then((res) => {
        setScheduleHistory(res?.data?.content || []);
      })
      .catch((error) => {
        console.log(error);
        setScheduleHistory([]);
      })
      .finally(() => {
        setHistoryLoading(false);
      });
  }, []);

  const handleCreateSchedule = () => {
    if (!scheduleDateTime) {
      window.alert("Please select schedule date and time");
      return;
    }
    if (!scheduleTemplateId.trim()) {
      window.alert("Please enter template ID");
      return;
    }

    const headers = { Authorization: getToken() };
    setIsSubmittingSchedule(true);

    axiosClient
      .post(
        "/v1/users/scheduler-whatsapp/schedules",
        {
          scheduledAt: scheduleDateTime,
          templateId: scheduleTemplateId.trim(),
          apiCode: scheduleApiCode.trim() || "IndianYadavs",
          campaignName: scheduleCampaignName.trim(),
          mediaUrl: scheduleMediaUrl.trim() || null,
          search: debouncedSearch,
          filter: {
            profileTypes: appliedFilter.profileTypes,
            hasDP: appliedFilter.hasDP,
            hasApp: appliedFilter.hasApp,
            genders: appliedFilter.genders,
            createdFrom: appliedFilter.createdFrom || null,
            createdTo: appliedFilter.createdTo || null,
          },
        },
        { headers }
      )
      .then(() => {
        setSendModalOpen(false);
        setScheduleDateTime("");
        setScheduleTemplateId("");
        setScheduleCampaignName("");
        setScheduleMediaUrl("");
        loadScheduleHistory();
      })
      .catch((error) => {
        window.alert(error?.response?.data || "Failed to create schedule");
      })
      .finally(() => {
        setIsSubmittingSchedule(false);
      });
  };

  const handleCancelSchedule = (scheduleId: number) => {
    if (!window.confirm("Cancel this schedule?")) return;
    const headers = { Authorization: getToken() };
    axiosClient
      .post(`/v1/users/scheduler-whatsapp/schedules/${scheduleId}/cancel`, {}, { headers })
      .then(() => loadScheduleHistory())
      .catch((error) => {
        window.alert(error?.response?.data || "Failed to cancel schedule");
      });
  };

  const handleDeleteSchedule = (scheduleId: number) => {
    if (!window.confirm("Delete this schedule?")) return;
    const headers = { Authorization: getToken() };
    axiosClient
      .delete(`/v1/users/scheduler-whatsapp/schedules/${scheduleId}`, { headers })
      .then(() => loadScheduleHistory())
      .catch((error) => {
        window.alert(error?.response?.data || "Failed to delete schedule");
      });
  };

  return (
    <div className="app-content pt-3 p-md-3 p-lg-4">
      <Paper sx={{ height: "80vh", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            p: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6" component="div">
            Scheduler Members
          </Typography>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search member"
            minWidth={{ xs: "100%", sm: 260, md: 320 }}
          />
          <Button
            size="small"
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={handleOpenFilter}
            sx={{
              background: "#212529",
              color: "#ffffff",
              "&:hover": { background: "#343a40" },
            }}
          >
            Filter
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SendRoundedIcon />}
            onClick={() => {
              setScheduleCampaignName(`campaign-${Date.now()}`);
              setSendModalOpen(true);
            }}
            disabled={totalRows === 0}
            sx={{
              background: "#15803d",
              color: "#ffffff",
              "&:hover": { background: "#166534" },
            }}
          >
            Send ({totalRows})
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryRoundedIcon />}
            onClick={() => {
              setHistoryModalOpen(true);
              loadScheduleHistory();
            }}
          >
            History
          </Button>
        </Box>

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {HEADERS.map((header) => (
                  <TableCell
                    key={header.field}
                    sx={{
                      ...headSx,
                      cursor: header.sortable ? "pointer" : "default",
                    }}
                    onClick={() => handleSort(header.field, header.sortable)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {header.label}
                      {header.sortable && sortField === header.field && (
                        <Typography sx={{ fontSize: "0.7rem", color: "#fff" }}>
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                  <TableCell sx={cellSx}>{row?.profile?.memberId || "N/A"}</TableCell>
                  <TableCell sx={cellSx}>{row.name || "—"}</TableCell>
                  <TableCell sx={cellSx}>
                    {row.countryCode || row.mobile
                      ? `${row.countryCode || ""} ${row.mobile || ""}`.trim()
                      : "—"}
                  </TableCell>
                  <TableCell sx={cellSx}>{row.hasDP ? "True" : "False"}</TableCell>
                  <TableCell sx={cellSx}>{row.gender || "—"}</TableCell>
                  <TableCell sx={cellSx}>
                    {row.created_at ? moment(row.created_at).format("DD/MM/YYYY, h:mm a") : "—"}
                  </TableCell>
                  <TableCell sx={cellSx}>{row.hasApp ? "True" : "False"}</TableCell>
                  <TableCell sx={cellSx}>
                    {row.profileType
                      ? row.profileType.charAt(0).toUpperCase() + row.profileType.slice(1)
                      : "Free"}
                  </TableCell>
                </TableRow>
              ))}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 2 }}>
                    No members found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Rows per page:
            </Typography>
            <CustomSelect
              value={pageModel.pageSize}
              onChange={(value) => setPageModel({ page: 0, pageSize: value as number })}
              options={pageSizeOptions}
              minWidth={90}
            />
          </Box>

          <Pagination
            count={totalPages}
            page={pageModel.page + 1}
            onChange={(_, page) => setPageModel((prev) => ({ ...prev, page: page - 1 }))}
            color="primary"
            size="small"
            disabled={isLoading}
            siblingCount={0}
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#212529",
                "&.Mui-selected": { backgroundColor: "#212529", color: "#ffffff" },
              },
            }}
          />

          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Total: {totalRows}
          </Typography>
        </Box>
      </Paper>

      <CustomModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filter Scheduler Members"
        maxWidth="sm"
        actions={
          <Box sx={{ display: "flex", gap: 1 }}>
            <CustomButton variant="secondary" onClick={handleClearFilter}>
              Clear
            </CustomButton>
            <CustomButton variant="secondary" onClick={() => setFilterModalOpen(false)}>
              Cancel
            </CustomButton>
            <CustomButton variant="primary" onClick={handleApplyFilter}>
              Apply
            </CustomButton>
          </Box>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Profile Type</InputLabel>
            <Select
              multiple
              value={draftFilter.profileTypes}
              onChange={(e) =>
                setDraftFilter((prev) => ({
                  ...prev,
                  profileTypes: e.target.value as string[],
                }))
              }
              input={<OutlinedInput label="Profile Type" />}
              renderValue={(selected) =>
                (selected as string[])
                  .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
                  .join(", ")
              }
            >
              {PROFILE_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={draftFilter.profileTypes.indexOf(type) > -1} />
                  <ListItemText primary={type.charAt(0).toUpperCase() + type.slice(1)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={draftFilter.hasDP}
                  onChange={(e) =>
                    setDraftFilter((prev) => ({ ...prev, hasDP: e.target.checked }))
                  }
                />
              }
              label="Has DP"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={draftFilter.hasApp}
                  onChange={(e) =>
                    setDraftFilter((prev) => ({ ...prev, hasApp: e.target.checked }))
                  }
                />
              }
              label="Has App"
            />
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Gender</InputLabel>
            <Select
              multiple
              value={draftFilter.genders}
              onChange={(e) =>
                setDraftFilter((prev) => ({
                  ...prev,
                  genders: e.target.value as string[],
                }))
              }
              input={<OutlinedInput label="Gender" />}
              renderValue={(selected) => (selected as string[]).join(", ")}
            >
              {GENDER_OPTIONS.map((gender) => (
                <MenuItem key={gender} value={gender}>
                  <Checkbox checked={draftFilter.genders.indexOf(gender) > -1} />
                  <ListItemText primary={gender} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Created From
              </Typography>
              <CustomInput
                type="date"
                value={draftFilter.createdFrom}
                onChange={(e) =>
                  setDraftFilter((prev) => ({
                    ...prev,
                    createdFrom: typeof e === "object" ? e.target.value : String(e),
                  }))
                }
                fullWidth
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Created To
              </Typography>
              <CustomInput
                type="date"
                value={draftFilter.createdTo}
                onChange={(e) =>
                  setDraftFilter((prev) => ({
                    ...prev,
                    createdTo: typeof e === "object" ? e.target.value : String(e),
                  }))
                }
                fullWidth
              />
            </Box>
          </Box>
        </Box>
      </CustomModal>

      <CustomModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        title="Schedule WhatsApp Send"
        maxWidth="sm"
        actions={
          <Box sx={{ display: "flex", gap: 1 }}>
            <CustomButton variant="secondary" onClick={() => setSendModalOpen(false)}>
              Cancel
            </CustomButton>
            <CustomButton variant="primary" onClick={handleCreateSchedule}>
              {isSubmittingSchedule ? "Submitting..." : "Submit"}
            </CustomButton>
          </Box>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2">Selected members: {totalRows}</Typography>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Date & Time
            </Typography>
            <CustomInput
              type="datetime-local"
              value={scheduleDateTime}
              onChange={(e) =>
                setScheduleDateTime(typeof e === "object" ? e.target.value : String(e))
              }
              fullWidth
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Template ID
            </Typography>
            <CustomInput
              value={scheduleTemplateId}
              onChange={(e) =>
                setScheduleTemplateId(typeof e === "object" ? e.target.value : String(e))
              }
              placeholder="e.g. 1399864151603159"
              fullWidth
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              API Code
            </Typography>
            <CustomInput
              value={scheduleApiCode}
              onChange={(e) =>
                setScheduleApiCode(typeof e === "object" ? e.target.value : String(e))
              }
              placeholder="e.g. IndianYadavs"
              fullWidth
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Campaign Name
            </Typography>
            <CustomInput
              value={scheduleCampaignName}
              onChange={(e) =>
                setScheduleCampaignName(typeof e === "object" ? e.target.value : String(e))
              }
              placeholder="e.g. fefreR434"
              fullWidth
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Media URL (optional)
            </Typography>
            <CustomInput
              value={scheduleMediaUrl}
              onChange={(e) =>
                setScheduleMediaUrl(typeof e === "object" ? e.target.value : String(e))
              }
              placeholder="https://..."
              fullWidth
            />
          </Box>
        </Box>
      </CustomModal>

      <CustomModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title="WhatsApp Schedule History"
        maxWidth="lg"
        actions={
          <CustomButton variant="secondary" onClick={() => setHistoryModalOpen(false)}>
            Close
          </CustomButton>
        }
      >
        <TableContainer sx={{ maxHeight: "60vh", overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>ID</TableCell>
                <TableCell sx={headSx}>Template ID</TableCell>
                <TableCell sx={headSx}>Campaign</TableCell>
                <TableCell sx={headSx}>Scheduled At</TableCell>
                <TableCell sx={headSx}>Status</TableCell>
                <TableCell sx={headSx}>Recipients</TableCell>
                <TableCell sx={headSx}>Sent</TableCell>
                <TableCell sx={headSx}>Failed</TableCell>
                <TableCell sx={headSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyLoading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              )}
              {!historyLoading && scheduleHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                    No schedules found
                  </TableCell>
                </TableRow>
              )}
              {!historyLoading &&
                scheduleHistory.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={cellSx}>{item.id}</TableCell>
                    <TableCell sx={cellSx}>{item.templateId || item.templateName || "—"}</TableCell>
                    <TableCell sx={cellSx}>{item.campaignName || "—"}</TableCell>
                    <TableCell sx={cellSx}>
                      {item.scheduledAt ? moment(item.scheduledAt).format("DD/MM/YYYY, h:mm a") : "—"}
                    </TableCell>
                    <TableCell sx={cellSx}>{item.status}</TableCell>
                    <TableCell sx={cellSx}>{item.totalRecipients || 0}</TableCell>
                    <TableCell sx={cellSx}>{item.sentCount || 0}</TableCell>
                    <TableCell sx={cellSx}>{item.failedCount || 0}</TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {item.status === "PENDING" && (
                          <Button size="small" variant="outlined" onClick={() => handleCancelSchedule(item.id)}>
                            Cancel
                          </Button>
                        )}
                        <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteSchedule(item.id)}>
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CustomModal>
    </div>
  );
}
